import {RouteProp, useFocusEffect, useNavigation, useRoute} from '@react-navigation/native';
import moment from 'moment';
import React, {useMemo, useState} from 'react';
import {
  SafeAreaView,
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Image,
  TextInput,
  Alert,
  ActivityIndicator,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import FailedPayment from '~components/FailedPayment/FailedPayment';
import {ModernHeader} from '~components/ModernHeader';
import ModernButton from '~components/ModernButton';
import {ModernPaymentCard, ModernPaymentSummary} from '~components/ModernPayment';
import StripePaymentWebStub from '~components/StripePaymentWebStub/StripePaymentWebStub';
import {SCREENS} from '~constants';
import {spacing, borderRadius, typography} from '~constants/design';
import {useCreatePaymentIntent} from '~hooks/event/useCreatePaymentIntent';
import {useGetEventById} from '~hooks/event/useGetEventById';
import {useGetActivePlanByEvent} from '~hooks/subscription/useGetActivePlanByEvent';
import {PaymentIntentResponse, Ticket, SavedCard} from '~types/api/event';
import {NavigationProps, RootStackParamsList} from '~types/navigation/navigation.type';
import {LONG_DATE_FORMAT} from '~Utils/Time';
import {useStripe} from '@stripe/stripe-react-native';
import {useGetUserAccount} from '~hooks/user/useGetUser';
import {useGetBusinessAccount} from '~hooks/business/useGetBusinessAccount';
import {Business} from '~types/api/business';
import {User} from '~types/api/user';
import {ChatType} from '~types/chat';
import FirebaseChatsService from '~services/FirebaseChats';
import {useUserStore} from '~providers/userStore/zustand';
import {useFetchSavedCards} from '~hooks/payment/useFetchSavedCards';
import {SavedCardsModal} from '~components/Payment/SavedCardsModal';

import firestore from '@react-native-firebase/firestore';
import {useAddUserEventStatus} from '~hooks/event/useAddUserEventStatus';
import {Notifier, NotifierComponents} from 'react-native-notifier';
import {useTheme} from '~contexts/ThemeContext';
import {ThemedStatusBar} from '~components/ThemedStatusBar';
import Animated, {FadeInDown, FadeInUp} from 'react-native-reanimated';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {LoadingSpinner} from '~components/ModernSpinner';
import {shadows} from '~constants/design';
import {useSubscriptionContext} from '~contexts/SubscriptionContext';
import {
  logEventCheckoutViewed,
  logEventPaymentStarted,
  logEventPaymentSucceeded,
  logEventPaymentFailed,
  logEventSignupCompleted,
  logEventSignupAbandoned,
} from '~Utils/firebaseAnalytics';
import {
  canAddPlusOneGuest,
  calculatePlusOneGuestCost,
  formatPlusOneGuestFee,
  formatPlusOneGuestNote,
  getPlusOneGuestRegistrationWarning,
  isPlusOneGuestExcludedForEvent,
  parsePlusOneGuestFee,
} from '~Utils/plusOneGuest';

const DATE_FORMAT = 'ddd ' + LONG_DATE_FORMAT;
export interface TicketType {
  id: string;
  type: string;
  price: number;
  description: string;
  quantity?: number;
}

const BuyTicket: React.FC = () => {
  const {colors} = useTheme();
  const {t} = useTranslation();
  const route = useRoute<RouteProp<RootStackParamsList, SCREENS.BUY_TICKET>>();
  const navigation = useNavigation<NavigationProps>();
  const {eventId} = route.params;
  const styles = StyleSheet.create({
    loaderContainer: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      justifyContent: 'center',
      alignItems: 'center',
    },
    strickThorughText: {
      textDecorationLine: 'line-through',
      textDecorationColor: colors.error,
      color: colors.gray400,
    },
    promoSection: {
      backgroundColor: colors.gray50,
      borderRadius: borderRadius.md,
      padding: spacing.sm,
      marginBottom: spacing.sm,
    },
    promoRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    promoInputContainer: {
      flex: 1,
      backgroundColor: colors.white,
      borderRadius: borderRadius.md,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: spacing.md,
      height: 48,
    },
    promoInput: {
      flex: 1,
      fontSize: typography.fontSize.base,
      color: colors.textPrimary,
    },
    applyButton: {
      backgroundColor: colors.eventInfluencer,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.sm,
      minWidth: 70,
      alignItems: 'center',
      justifyContent: 'center',
    },
    removeButton: {
      backgroundColor: colors.error,
    },
    applyButtonText: {
      color: colors.white,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
    },
    appliedText: {
      fontSize: typography.fontSize.sm,
      color: colors.statusGreen,
      marginTop: spacing.sm,
      fontWeight: '500',
      textAlign: 'center',
    },
    soldoutText: {
      alignSelf: 'center',
      color: colors.error,
      marginTop: 15,
      fontWeight: 'bold',
      textAlign: 'center',
    },
    container: {flex: 1, backgroundColor: colors.gray50},
    eventDetails: {
      marginHorizontal: spacing.sm,
      marginTop: spacing.xs,
      backgroundColor: colors.white,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 1},
      shadowOpacity: 0.03,
      shadowRadius: 4,
      elevation: 1,
      borderWidth: 1,
      borderColor: colors.border,
    },
    eventImageContainer: {
      alignItems: 'center',
      marginBottom: spacing.sm,
    },
    eventImage: {
      width: '100%',
      height: 140,
      borderRadius: borderRadius.sm,
      backgroundColor: colors.gray100,
    },
    eventInfo: {
      alignItems: 'center',
    },
    eventTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      textAlign: 'center',
      marginBottom: spacing.xs,
    },
    eventLocation: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      textAlign: 'center',
      marginBottom: spacing.xs,
    },
    eventTime: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
      textAlign: 'center',
      fontWeight: '500',
    },
    listContainer: {
      paddingHorizontal: spacing.sm,
      paddingTop: spacing.sm,
    },
    ticketItemContainer: {
      marginBottom: spacing.sm,
      padding: spacing.md,
      backgroundColor: colors.white,
      borderRadius: borderRadius.md,
      shadowColor: colors.black,
      shadowOpacity: 0.03,
      shadowOffset: {width: 0, height: 1},
      shadowRadius: 4,
      elevation: 1,
      borderWidth: 1,
      borderColor: colors.border,
    },
    ticketItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: spacing.xs,
    },
    ticketInfo: {
      flex: 1,
      marginRight: spacing.sm,
    },
    ticketType: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textPrimary,
      marginBottom: spacing.xs,
    },
    ticketDescription: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
      lineHeight: 16,
    },
    quantityControls: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.gray50,
      borderRadius: borderRadius.lg,
      padding: spacing.xs,
    },
    button: {
      width: 44,
      height: 44,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.white,
      borderRadius: borderRadius.lg,
      shadowColor: colors.black,
      shadowOpacity: 0.1,
      shadowOffset: {width: 0, height: 2},
      shadowRadius: 4,
      elevation: 2,
      borderWidth: 1,
      borderColor: colors.border,
    },
    buttonPressed: {
      backgroundColor: colors.eventInfluencer,
      transform: [{scale: 0.95}],
    },
    buttonDisabled: {
      backgroundColor: colors.gray200,
      shadowOpacity: 0,
      elevation: 0,
    },
    buttonText: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.eventInfluencer,
    },
    buttonTextPressed: {
      color: colors.white,
    },
    buttonTextDisabled: {
      color: colors.gray400,
    },
    quantity: {
      marginHorizontal: spacing.md,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      minWidth: 32,
      textAlign: 'center',
    },
    footer: {
      padding: spacing.md,
      backgroundColor: colors.white,
      borderTopWidth: 1,
      borderTopColor: colors.border,
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: -1},
      shadowOpacity: 0.03,
      shadowRadius: 4,
      elevation: 2,
    },
    paymentSummary: {
      backgroundColor: colors.gray50,
      borderRadius: borderRadius.md,
      padding: spacing.sm,
      marginBottom: spacing.sm,
    },
    summaryRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.xs,
    },
    summaryLabel: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    summaryValue: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textPrimary,
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderTopWidth: 1,
      borderTopColor: colors.border,
      paddingTop: spacing.sm,
      marginTop: spacing.sm,
    },
    totalLabel: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
    },
    totalAmount: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.eventInfluencer,
    },
    payButton: {
      backgroundColor: colors.eventInfluencer,
      paddingVertical: spacing.lg,
      borderRadius: borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: colors.eventInfluencer,
      shadowOffset: {width: 0, height: 4},
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 4,
    },
    payButtonDisabled: {
      backgroundColor: colors.gray400,
      shadowOpacity: 0,
      elevation: 0,
    },
    payButtonText: {
      color: colors.white,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
    },
    guestNote: {
      backgroundColor: colors.statusBlue + '15',
      borderRadius: borderRadius.md,
      padding: spacing.sm,
      marginBottom: spacing.sm,
      borderLeftWidth: 3,
      borderLeftColor: colors.statusBlue,
    },
    guestNoteText: {
      fontSize: typography.fontSize.xs,
      color: colors.statusBlue,
      lineHeight: 16,
      fontWeight: '500',
    },
  });
  const {data: fetchedItem, refetch: refetchEvent, isLoading: isEventLoading} = useGetEventById(eventId);
  const {
    data: eventActivePlan,
    isLoading: isEventActivePlanLoading,
    error: eventActivePlanError,
    refetch: refetchEventActivePlan,
  } = useGetActivePlanByEvent(eventId.toString());
  const [quantities, setQuantities] = useState<Record<string, number>>(
    (fetchedItem?.tickets || []).reduce(
      (acc, ticket, index) => ({
        ...acc,
        [ticket.name]: index === 0 && ticket.capacity - ticket.sold > 0 ? 1 : 0,
      }),
      {},
    ),
  );
  const [isModalVisible, setModalVisible] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState('');
  const [promoDiscountPer, setPromoDiscountPer] = useState(0);
  const [promoId, setPromoId] = useState('');
  const [plusOneGuestCount, setPlusOneGuestCount] = useState(0); // Number of +1 guests
  const [isLoading, setIsLoading] = useState(false);
  const [showWebPayment, setShowWebPayment] = useState(false);
  const [webPaymentSecret, setWebPaymentSecret] = useState('');
  const [selectedTicketsForPayment, setSelectedTicketsForPayment] = useState<any>(null);
  const [paymentIntentData, setPaymentIntentData] = useState<PaymentIntentResponse | null>(null);
  const [showSavedCardsModal, setShowSavedCardsModal] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [pendingPaymentData, setPendingPaymentData] = useState<{
    body: any;
    selectedTickets: any;
  } | null>(null);
  const {mutateAsync: addPaymentIntent} = useCreatePaymentIntent();
  const {initPaymentSheet, presentPaymentSheet} = useStripe();
  const {user} = useUserStore();
  const {data: userAccount} = useGetUserAccount(user?.uid || '');
  const {data: hostUser} = useGetUserAccount(fetchedItem?.host_id || '');
  const {data: hostBusiness} = useGetBusinessAccount(fetchedItem?.host_id || '');
  const {mutateAsync: addEventUserStatus} = useAddUserEventStatus();
  
  // Fetch saved cards
  const {
    data: savedCardsData,
    isLoading: cardsLoading,
  } = useFetchSavedCards({
    enabled: true,
  });
  const host = !(hostUser as unknown as {detail: string})?.detail ? hostUser : hostBusiness;
  const isPyxiSelect = host?.uid === 'CmCCvWvpRVhhFlHdgU9QE6hbun82';
  const {setIsTabBarDisabled} = useTabBar();

    useFocusEffect(
      React.useCallback(() => {
        refetchEventActivePlan();
      }, [refetchEventActivePlan]),
    );

  React.useLayoutEffect(() => {
    setTimeout(() => {
      setIsTabBarDisabled(true);
    }, 400);
    setTimeout(() => {
      setIsTabBarDisabled(true);
    }, 500);
  }, [setIsTabBarDisabled]);

  const handleRetry = () => {
    setModalVisible(false);
    // Retry logic here
    console.log('Retry pressed');
  };
  const onApplyPromo = () => {
    if (calculateTotal() <= 0) {
      Alert.alert('Please add ticket first.');
      return;
    }
    if (promoCode.trim() === '') {
      Alert.alert('Please enter a valid promo code.');
      return;
    }
    // Example logic to validate promo code
    if (appliedPromo) {
      setAppliedPromo('');
      setPromoCode('');
      Alert.alert(`Promo code "${promoCode}" removed!`);
    } else {
      const promoCodeList = fetchedItem?.promo_codes;
      const isPromocodeApply = promoCodeList?.filter(item => item.name == promoCode);
      if (isPromocodeApply && isPromocodeApply?.length > 0) {
        setAppliedPromo(promoCode);
        setPromoId(isPromocodeApply[0].promo_code_id);
        setPromoDiscountPer(isPromocodeApply[0].discount_percentage);
        Alert.alert(`Promo code "${promoCode}" applied!`);
      } else {
        Alert.alert('Promo code is invalid!');
      }
    }
  };

  const handleClose = () => {
    setModalVisible(false);
  };

  useFocusEffect(
    React.useCallback(() => {
      refetchEvent();
    }, [refetchEvent]),
  );

  // Set default quantity to 1 for the first available ticket when data loads
  React.useEffect(() => {
    if (fetchedItem?.tickets && fetchedItem.tickets.length > 0) {
      const firstAvailableTicket = fetchedItem.tickets.find(ticket => ticket.capacity - ticket.sold > 0);
      if (firstAvailableTicket) {
        setQuantities(prev => ({
          ...prev,
          [firstAvailableTicket.name]: prev[firstAvailableTicket.name] || 1,
        }));
      }
    }
  }, [fetchedItem?.tickets]);

  const handleIncrement = (id: string, capacity: number) => {
    setQuantities(prev => {
      const currentQuantity = prev[id] || 0;
      const updatedQuantity = currentQuantity + 1;
      if (updatedQuantity > 2 && isPyxiSelect) {
        Alert.alert('Alert', 'You can select maximum 2 tickets.');
        return prev;
      }
      if (currentQuantity + 1 > capacity) {
        return prev;
      }
      return {...prev, [id]: currentQuantity + 1};
    });
  };

  const handleDecrement = (id: string) => {
    setQuantities(prev => ({...prev, [id]: Math.max((prev[id] || 0) - 1, 0)}));
  };

  const calculateTotal = (): number => {
    // If user has unlimited event access through subscription, tickets are free
    if (eventActivePlan?.unlimited_for_event) {
      let totalCost = 0;
      
      // Only add +1 guest cost if applicable (tickets are free through subscription)
      if (
        plusOneGuestCount > 0 &&
        eventActivePlan?.plan?.feature_list?.bring_plus_one_fee &&
        !isPlusOneGuestExcludedForEvent(fetchedItem?.event_service_type, undefined)
      ) {
        const feePerGuest = parsePlusOneGuestFee(eventActivePlan.plan.feature_list.bring_plus_one_fee);
        totalCost = (feePerGuest / 100) * plusOneGuestCount; // Convert minor units back to major units
      }
      
      return totalCost;
    }

    // Regular pricing for non-subscribed users (full ticket price only, no guest fees)
    // +1 guest feature is only available to subscription users
    const ticketsTotal = (fetchedItem?.tickets || []).reduce((total, ticket) => {
      const price = ticket.discounted_price ? ticket.discounted_price : ticket.price;
      return total + (quantities[ticket.name] || 0) * price;
    }, 0);

    return ticketsTotal;
  };

  const calculateQunatity = (): number => {
    return (fetchedItem?.tickets || []).reduce((total, ticket) => {
      return total + (quantities[ticket.name] || 0);
    }, 0);
  };

  const calculatePercentage = (percentage: number): number => {
    const total = calculateTotal();
    return total - (total * percentage) / 100;
  };

  const isCompleted = React.useRef(false);

  React.useEffect(() => {
    if (fetchedItem) {
      logEventCheckoutViewed(
        eventId.toString(),
        fetchedItem.event_service_type || 'unknown',
        calculateTotal(),
        fetchedItem.tickets?.[0]?.currency || 'GBP',
      );
    }
  }, [fetchedItem]);

  React.useEffect(() => {
    return () => {
      if (fetchedItem && !isCompleted.current) {
        logEventSignupAbandoned(eventId.toString(), fetchedItem.event_service_type || 'unknown', 'Checkout');
      }
    };
  }, [fetchedItem]);

  const initializePaymentSheet = async (data: PaymentIntentResponse) => {
    const {error} = await initPaymentSheet({
      merchantDisplayName: 'Pyxi',
      paymentIntentClientSecret: data.client_secret, // retrieve this from your server
      returnURL: 'https://partner.pyxi.ai/payment-return',
    });

    if (error) {
      console.error('Error initializing Payment Sheet:', error);
      return false;
    }

    console.log('Payment Sheet initialized successfully');
    return true;
  };

  const openPaymentSheet = async (selectedTickets: any, data: PaymentIntentResponse) => {
    if (Platform.OS === 'web') {
      // On web, show the payment stub modal
      setWebPaymentSecret(data.client_secret);
      setPaymentIntentData(data);
      setSelectedTicketsForPayment(selectedTickets);
      setShowWebPayment(true);
      return;
    }

    // Native payment sheet flow
    const {error} = await presentPaymentSheet();

    if (error) {
      setIsLoading(false);
      logEventPaymentFailed(eventId.toString(), fetchedItem?.event_service_type || 'unknown', error.message || error.code);
      Alert.alert('Payment failed', error.message);
      console.error('Error presenting Payment Sheet:', error.message);
    } else {
      setIsLoading(false);
      isCompleted.current = true;
      logEventPaymentSucceeded(
        eventId.toString(),
        fetchedItem?.event_service_type || 'unknown',
        calculateTotal(),
        fetchedItem?.tickets?.[0]?.currency || 'GBP',
      );
      logEventSignupCompleted(eventId.toString(), fetchedItem?.event_service_type || 'unknown');
      navigation.replace(SCREENS.PAYMENT_SUCCESS, {
        eventId: eventId,
        eventData: fetchedItem,
        selectedTickets,
        order_id: data.order_id,
      });
      console.log('Payment successful!');
    }
  };

  const onJoinClick = async () => {
    const selectedTickets = (fetchedItem?.tickets || [])
      .filter(ticket => (quantities[ticket.name] || 0) > 0)
      .map(ticket => ({
        ...ticket,
        quantity: quantities[ticket.name] || 0,
        count: quantities[ticket.name] || 0,
      }));
    setIsLoading(true);
    try {
      const data: any = await addEventUserStatus({
        event_id: eventId.toString(),
        count: calculateQunatity().toString(),
      });
      if (data && data?.status == 'accepted') {
        isCompleted.current = true;
        logEventSignupCompleted(eventId.toString(), fetchedItem?.event_service_type || 'unknown');
        navigation.replace(SCREENS.PAYMENT_SUCCESS, {
          eventId: eventId,
          eventData: fetchedItem,
          selectedTickets,
          order_id: data.order?.order_id,
        });
      } else if (data && data?.status == 'pending') {
        isCompleted.current = true;
        logEventSignupCompleted(eventId.toString(), fetchedItem?.event_service_type || 'unknown');
        Notifier.showNotification({
          title: 'Your request to join the event has been sent to the organiser. Please wait for approval.',
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'info',
          },
        });
        navigation.goBack();
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const onPayClick = async () => {
    const plusOneCount = plusOneGuestCount || 0;
    const selectedTickets = (fetchedItem?.tickets || [])
      .filter(ticket => (quantities[ticket.name] || 0) > 0)
      .map(ticket => ({
        ...ticket,
        quantity: quantities[ticket.name] + plusOneCount || 0,
        count: quantities[ticket.name] + plusOneCount || 0,
      }));

    const body: any = {
      tickets: selectedTickets,
      eventId: eventId,
    };
    if (promoId) {
      body.promo_code_id = promoId;
    }

    // Add +1 guest information if applicable
    if (
      plusOneGuestCount > 0 &&
      eventActivePlan?.plan?.feature_list?.bring_plus_one_fee &&
      eventActivePlan?.plan?.feature_list?.bring_plus_one_fee !== 'Free' &&
      !isPlusOneGuestExcludedForEvent(fetchedItem?.event_service_type, undefined)
    ) {
      const feePerGuest = parsePlusOneGuestFee(eventActivePlan.plan.feature_list.bring_plus_one_fee);
      body.plus_one_guest_count = plusOneGuestCount;
      body.plus_one_guest_fee = feePerGuest; // Fee in minor units (pence for GBP)
    }

    // Check if user has saved cards
    if (savedCardsData && savedCardsData.cards && savedCardsData.cards.length > 0) {
      setPendingPaymentData({ body, selectedTickets });
      setShowSavedCardsModal(true);
      return;
    }

    // No saved cards, proceed directly
    await processTicketPayment(body, selectedTickets);
  };

  const handleSelectSavedCard = async (card: SavedCard) => {
    console.log('Selected card for ticket:', card.id, `${card.card_brand} ****${card.card_last4}`);
    setSelectedPaymentMethodId(card.payment_method_id);
    if (pendingPaymentData) {
      await processTicketPayment(pendingPaymentData.body, pendingPaymentData.selectedTickets, card.payment_method_id);
    }
  };

  const processTicketPayment = async (body: any, selectedTickets: any, paymentMethodId?: string) => {
    try {
      setIsLoading(true);
      logEventPaymentStarted(eventId.toString(), fetchedItem?.event_service_type || 'unknown', 'Stripe');

      // Add payment method ID if card was selected
      if (paymentMethodId) {
        body.payment_method_id = paymentMethodId;
      }

      // If event is included in subscription, use subscription booking
      if (eventActivePlan?.can_book_event) {
        if (eventActivePlan.unlimited_for_event) {
          const response = await addPaymentIntent(body);
          if (response?.order_id) {
            setIsLoading(false);
            navigation.replace(SCREENS.PAYMENT_SUCCESS, {
              eventId: eventId,
              eventData: fetchedItem,
              selectedTickets,
              order_id: response.order_id,
            });
            return;
          } else {
            throw new Error('Failed to create subscription booking');
          }
        } else {
          Alert.alert(
            'Subscription Booking',
            'Your booking is outside your paid subscription. Confirm your subscription for this month to complete your booking or proceed with a one-time fee',
            [
              {
                text: 'Cancel',
                onPress: () => {
                  setIsLoading(false);
                },
                style: 'cancel',
              },
              {
                text: 'Subscribed for this month',
                onPress: async () => {
                  // Handle proceed to subscription confirmation
                  body.confirm_advance = true;
                  const response = await addPaymentIntent(body);
                  if (response?.order_id) {
                    setIsLoading(false);
                    navigation.replace(SCREENS.PAYMENT_SUCCESS, {
                      eventId: eventId,
                      eventData: fetchedItem,
                      selectedTickets,
                      order_id: response.order_id,
                    });
                    return;
                  } else {
                    throw new Error('Failed to create subscription booking');
                  }
                },
              },
              {
                text: 'One-time Fee',
                onPress: async () => {
                  // Handle one-time fee payment
                  try {
                    body.onetime_pass = true;
                    const data = await addPaymentIntent(body);
                    if (data?.order_id) {
            setIsLoading(false);
            navigation.replace(SCREENS.PAYMENT_SUCCESS, {
              eventId: eventId,
              eventData: fetchedItem,
              selectedTickets,
              order_id: data.order_id,
            });
            return;
          }
                    const isInitialized = await initializePaymentSheet(data);
                    if (isInitialized) {
                      await openPaymentSheet(selectedTickets, data);
                    }
                    setIsLoading(false);
                    return;
                  } catch (error: any) {
                    console.error('Error processing one-time fee:', error);
                    Alert.alert('Error', error.message || 'Failed to process payment');
                    setIsLoading(false);
                    throw new Error('Failed to create subscription booking');
                    return;
                  }
                },
                style: 'default',
              },
            ],
          );
          return;
        }
      }

      // Regular payment flow
      const data = await addPaymentIntent(body);

                    if (data?.order_id && paymentMethodId) {
            setIsLoading(false);
            navigation.replace(SCREENS.PAYMENT_SUCCESS, {
              eventId: eventId,
              eventData: fetchedItem,
              selectedTickets,
              order_id: data.order_id,
            });
            return;
          }
      const isInitialized = await initializePaymentSheet(data);
      if (isInitialized) {
        await openPaymentSheet(selectedTickets, data);
      }
      setIsLoading(false);
      return;
    } catch (error: any) {
      console.log(error, 'Failed to initialize payment!');
      console.log(error.message, 'Failed to initialize payment!');

      Alert.alert(error.message || 'Failed to initialize payment!');
      setIsLoading(false);
    }
  };

  const goBackHandler = () => {
    navigation.goBack();
  };

  const ticketAvailable = useMemo(() => {
    const ticketList = fetchedItem?.tickets?.filter(item => {
      return item.capacity - item.sold > 0;
    });
    return ticketList?.length;
  }, [fetchedItem]);

  const isEventIncludedInSubscription = React.useMemo(() => {
    return eventActivePlan?.can_book_event || false;
  }, [eventActivePlan]);

  const onChatClick = async () => {
    if (host && fetchedItem) {
      setIsLoading(true);

      //@ts-ignore
      const chatId = await FirebaseChatsService.createOrganisationChat({
        user_id1: user?.uid || '',
        user_id2: host?.uid,
        user_name1: `${userAccount?.first_name} ${userAccount?.last_name || ''}`,
        user_name2: (host as Business)?.name
          ? (host as Business)?.name
          : (host as unknown as User)?.last_name
            ? `${(host as unknown as User)?.first_name} ${(host as unknown as User)?.last_name || ''}`
            : (host as unknown as User)?.first_name || '',
        user_image: userAccount?.photo + '',
        event: fetchedItem,
      });

      const chatRef = firestore().collection('chats').doc(chatId);
      const doc = await chatRef.get();
      if (doc.exists) {
        const chatData = doc.data() as ChatType;
        const updatedMessages = chatData.history.map((message: any) => {
          if (!message.readUserIds?.includes(user?.uid || '')) {
            console.log('Updating message:', message);
            return {...message, readUserIds: [...(message.readUserIds || []), user?.uid || '']};
          }
          return message;
        });

        await chatRef.update({history: updatedMessages});
      }

      navigation.navigate(SCREENS.CHAT_STACK, {
        key: chatId,
        fromEventId: fetchedItem?.event_id,
        fromScreen: 'BuyTicket',
      });
      setIsLoading(false);
    }
  };

     const CURRENCY_SYMBOLS: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      CAD: '$',
      AUD: '$',
      NZD: '$',
      JPY: '¥',
      CNY: '¥',
      INR: '₹',
      KRW: '₩',
      RUB: '₽',
      TRY: '₺',
      VND: '₫',
      ILS: '₪',
      BRL: 'R$',
      ZAR: 'R',
      NGN: '₦',
      MXN: '$',
      CHF: 'CHF',
      SEK: 'kr',
      NOK: 'kr',
      DKK: 'kr',
      PLN: 'zł',
      CZK: 'Kč',
      HUF: 'Ft',
      THB: '฿',
      PHP: '₱',
      IDR: 'Rp',
      MYR: 'RM',
      SGD: 'S$',
      HKD: 'HK$',
      TWD: 'NT$',
      ARS: '$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
    };

  const renderTicketItem = ({item, index}: {item: Ticket; index: number}) => (
    <Animated.View entering={FadeInDown.delay(300 + index * 100).duration(600)} style={styles.ticketItemContainer}>
      <View style={styles.ticketItem}>
        <View style={styles.ticketInfo}>
          <Text style={styles.ticketType}>{item.name}</Text>
          {item.description && <Text style={styles.ticketDescription}>{item.description}</Text>}
          <View style={{flexDirection: 'row', alignItems: 'center', marginTop: spacing.xs}}>
            {item.price > 0 && (
              <>
                <Text
                  style={{
                    textDecorationLine: isEventIncludedInSubscription ? 'line-through' : 'none',
                    fontSize: typography.fontSize.lg,
                    fontWeight: typography.fontWeight.bold,
                    color: colors.eventInfluencer,
                  }}>
                  {CURRENCY_SYMBOLS[item.currency] || item.currency}
                  {item.discounted_price && item.price !== item.discounted_price ? item.discounted_price : item.price}
                </Text>
                {isEventIncludedInSubscription && (
                  <Text
                    style={{
                      fontSize: typography.fontSize.lg,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.eventInfluencer,
                    }}>
                    {' '}
                    0
                  </Text>
                )}
                {item.discounted_price && item.price !== item.discounted_price && (
                  <Text style={[styles.strickThorughText, {marginLeft: spacing.xs}]}>
                    {CURRENCY_SYMBOLS[item.currency] || item.currency}
                    {item.price}
                  </Text>
                )}
              </>
            )}
            {item.price === 0 && (
              <Text
                style={{
                  fontSize: typography.fontSize.lg,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.statusGreen,
                }}>
                FREE
              </Text>
            )}
          </View>
        </View>
        {!isEventIncludedInSubscription && item.capacity - item.sold > 0 ? (
          <View style={styles.quantityControls}>
            <TouchableOpacity
              style={[styles.button, (quantities[item.name] || 0) === 0 && styles.buttonDisabled]}
              onPress={() => handleDecrement(item.name)}
              disabled={(quantities[item.name] || 0) === 0}
              activeOpacity={0.7}
              accessibilityLabel="Decrease quantity"
              accessibilityRole="button">
              <Text style={[styles.buttonText, (quantities[item.name] || 0) === 0 && styles.buttonTextDisabled]}>
                −
              </Text>
            </TouchableOpacity>
            <Text style={styles.quantity}>{quantities[item.name] || 0}</Text>
            <TouchableOpacity
              style={styles.button}
              onPress={() => handleIncrement(item.name, item.capacity - item.sold)}
              activeOpacity={0.7}
              accessibilityLabel="Increase quantity"
              accessibilityRole="button">
              <Text style={styles.buttonText}>+</Text>
            </TouchableOpacity>
          </View>
        ) : !isEventIncludedInSubscription ? (
          <View
            style={{
              backgroundColor: colors.error + '20',
              paddingHorizontal: spacing.md,
              paddingVertical: spacing.sm,
              borderRadius: borderRadius.md,
            }}>
            <Text style={[styles.soldoutText, {margin: 0}]}>Sold out</Text>
          </View>
        ) : null}
      </View>
    </Animated.View>
  );

  const getCurrency = () => {
    if (fetchedItem?.tickets && fetchedItem?.tickets.length > 0) {
      const currencyValue = fetchedItem?.tickets[0].currency;
      return CURRENCY_SYMBOLS[currencyValue] || currencyValue
    }
    return '$';
  };

  return (
    <SafeAreaView style={styles.container}>
      <ThemedStatusBar />
      
      {/* Web Payment Stub Modal */}
      <StripePaymentWebStub
        visible={showWebPayment}
        clientSecret={webPaymentSecret}
        onPaymentSuccess={(data) => {
          setShowWebPayment(false);
          setIsLoading(false);
          // Navigate to success page
          navigation.replace(SCREENS.PAYMENT_SUCCESS, {
            eventId: eventId,
            eventData: fetchedItem,
            selectedTickets: selectedTicketsForPayment,
            order_id: paymentIntentData?.order_id || '',
          });
        }}
        onPaymentError={(error) => {
          setIsLoading(false);
          Alert.alert('Payment Failed', error);
        }}
        onClose={() => {
          setShowWebPayment(false);
          setIsLoading(false);
        }}
      />

      <Animated.View entering={FadeInDown.duration(500)}>
        <ModernHeader
          title="Book your seats"
          subtitle={fetchedItem?.name}
          onBackPress={goBackHandler}
          variant="elevated"
        />
      </Animated.View>
      <KeyboardAvoidingView style={{flex: 1}} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          style={{flex: 1}}
          contentContainerStyle={{paddingBottom: spacing.lg}}
          showsVerticalScrollIndicator={false}>
          {/* Event Details */}
          <Animated.View entering={FadeInUp.delay(200).duration(600)} style={styles.eventDetails}>
            <View style={styles.eventImageContainer}>
              <Image source={{uri: fetchedItem?.image_url}} style={styles.eventImage} />
            </View>
            <View style={styles.eventInfo}>
              <Text style={styles.eventTitle}>{fetchedItem?.name}</Text>
              <Text style={styles.eventLocation}>
                📍{' '}
                {fetchedItem?.event_service_type == 'THE_REST_TABLE'
                  ? fetchedItem?.address_name
                  : fetchedItem?.coord_address}
              </Text>
              <Text style={styles.eventTime}>
                🗓️ {fetchedItem?.start_date ? moment.utc(fetchedItem?.start_date).format(DATE_FORMAT) : 'N/A'} -{' '}
                {fetchedItem?.end_date ? moment.utc(fetchedItem?.end_date).format(DATE_FORMAT) : 'N/A'}
              </Text>
            </View>
          </Animated.View>

          {/* Tickets Section */}
          <View style={styles.listContainer}>
            {fetchedItem?.tickets?.map((item, index) => renderTicketItem({item, index}))}
          </View>

          {/* +1 Guest Section */}
          {canAddPlusOneGuest(fetchedItem?.event_service_type, !!eventActivePlan?.can_book_event, eventActivePlan?.plan?.feature_list?.bring_plus_one_fee ? {
            enabled: true,
            fee: eventActivePlan.plan.feature_list.bring_plus_one_fee,
            description: `Bring a +1 guest${eventActivePlan.plan.feature_list.bring_plus_one_fee === 'Free' ? ' for free' : ` for ${eventActivePlan.plan.feature_list.bring_plus_one_fee}`}`,
            registrationDaysRequired: 3,
            excludedEventTypes: "HOUSEWARMING",
          } : undefined) && (
            <Animated.View
              entering={FadeInUp.delay(350).duration(600)}
              style={{
                marginHorizontal: spacing.md,
                padding: spacing.lg,
                backgroundColor: colors.white,
                borderRadius: borderRadius.xl,
                borderWidth: 1,
                borderColor: colors.statusBlue + '20',
                shadowColor: colors.statusBlue,
                shadowOffset: {width: 0, height: 4},
                shadowOpacity: 0.08,
                shadowRadius: 12,
                elevation: 3,
              }}>
              {/* Header */}
              <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: spacing.lg}}>
                <View style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: colors.statusBlue + '15',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginRight: spacing.md,
                }}>
                  <Text style={{fontSize: 22}}>👥</Text>
                </View>
                <View style={{flex: 1}}>
                  <Text
                    style={{
                      fontSize: typography.fontSize.lg,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                      marginBottom: spacing.xs,
                    }}>
                    Bring a +1 Guest
                  </Text>
                  <Text
                    style={{
                      fontSize: typography.fontSize.xs,
                      color: colors.textSecondary,
                    }}>
                    {eventActivePlan?.plan?.feature_list?.bring_plus_one_fee === 'Free' 
                      ? 'Complimentary for your plan'
                      : `Only ${formatPlusOneGuestFee(eventActivePlan?.plan?.feature_list?.bring_plus_one_fee, getCurrency())}`}
                  </Text>
                </View>
              </View>

              {/* Description */}
              <Text
                style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                  marginBottom: spacing.lg,
                  lineHeight: 20,
                }}>
                {formatPlusOneGuestNote(
                  eventActivePlan?.plan?.feature_list?.bring_plus_one_fee,
                  getCurrency(),
                  3
                )}
              </Text>

              {/* Guest Quantity Card */}
              <View style={{
                backgroundColor: colors.gray50,
                borderRadius: borderRadius.lg,
                padding: spacing.md,
                marginBottom: spacing.lg,
                borderWidth: 1,
                borderColor: colors.border,
              }}>
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <Text style={{
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.semibold,
                    color: colors.textPrimary,
                  }}>
                    How many guests?
                  </Text>
                  
                  {/* Quantity Controls */}
                  <View style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: colors.white,
                    borderRadius: borderRadius.lg,
                    paddingHorizontal: spacing.xs,
                    paddingVertical: spacing.xs,
                    borderWidth: 1,
                    borderColor: colors.border,
                  }}>
                    <TouchableOpacity
                      style={[
                        styles.button,
                        {
                          width: 36,
                          height: 36,
                          backgroundColor: plusOneGuestCount === 0 ? colors.gray200 : colors.white,
                        },
                        plusOneGuestCount === 0 && styles.buttonDisabled,
                      ]}
                      onPress={() => setPlusOneGuestCount(Math.max(0, plusOneGuestCount - 1))}
                      disabled={plusOneGuestCount === 0}
                      activeOpacity={0.7}>
                      <Text
                        style={[
                          styles.buttonText,
                          plusOneGuestCount === 0 && styles.buttonTextDisabled,
                          {fontSize: 18, fontWeight: 'bold'},
                        ]}>
                        −
                      </Text>
                    </TouchableOpacity>

                    <View style={{
                      minWidth: 50,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Text
                        style={{
                          fontSize: typography.fontSize.xl,
                          fontWeight: typography.fontWeight.bold,
                          color: colors.eventInfluencer,
                        }}>
                        {plusOneGuestCount}
                      </Text>
                    </View>

                    <TouchableOpacity
                      style={[
                        styles.button,
                        {
                          width: 36,
                          height: 36,
                          backgroundColor: colors.eventInfluencer + '10',
                        },
                      ]}
                      onPress={() => {
                        if (plusOneGuestCount < 1) {
                          setPlusOneGuestCount(plusOneGuestCount + 1);
                        }
                      }}
                      activeOpacity={0.7}>
                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 'bold',
                          color: colors.eventInfluencer,
                        }}>
                        +
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Cost Breakdown */}
              {plusOneGuestCount > 0 && (
                <View style={{
                  backgroundColor: eventActivePlan?.plan?.feature_list?.bring_plus_one_fee === 'Free' 
                    ? colors.success + '10'
                    : colors.statusBlue + '10',
                  borderRadius: borderRadius.lg,
                  padding: spacing.md,
                  marginBottom: spacing.lg,
                  borderLeftWidth: 4,
                  borderLeftColor: eventActivePlan?.plan?.feature_list?.bring_plus_one_fee === 'Free' 
                    ? colors.success
                    : colors.statusBlue,
                }}>
                  <View style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <Text style={{
                      fontSize: typography.fontSize.sm,
                      color: colors.textSecondary,
                      flex: 1,
                    }}>
                      {plusOneGuestCount} guest{plusOneGuestCount > 1 ? 's' : ''} × {formatPlusOneGuestFee(eventActivePlan?.plan?.feature_list?.bring_plus_one_fee, getCurrency())}
                    </Text>
                    <Text
                      style={{
                        fontSize: typography.fontSize.base,
                        fontWeight: typography.fontWeight.bold,
                        color: eventActivePlan?.plan?.feature_list?.bring_plus_one_fee === 'Free' 
                          ? colors.success
                          : colors.statusBlue,
                      }}>
                      {getCurrency()}
                      {(calculatePlusOneGuestCost(plusOneGuestCount, {
                        enabled: true,
                        fee: eventActivePlan.plan.feature_list.bring_plus_one_fee,
                        description: '',
                        registrationDaysRequired: 3,
                      }) / 100).toFixed(2)}
                    </Text>
                  </View>
                </View>
              )}

              {/* Registration Requirement Warning */}
              <View style={{
                backgroundColor: colors.error + '08',
                borderRadius: borderRadius.lg,
                padding: spacing.md,
                borderLeftWidth: 4,
                borderLeftColor: colors.error,
              }}>
                <View style={{flexDirection: 'row', alignItems: 'flex-start'}}>
                  <Text
                    style={{
                      fontSize: typography.fontSize.xs,
                      color: colors.textSecondary,
                      lineHeight: 16,
                      fontWeight: '500',
                      flex: 1,
                    }}>
                    {getPlusOneGuestRegistrationWarning(3)}
                  </Text>
                </View>
              </View>
            </Animated.View>
          )}

          {/* Subscription Benefits Section */}
          {isEventIncludedInSubscription ? (
            <Animated.View
              entering={FadeInUp.delay(300).duration(600)}
              style={{
                marginHorizontal: spacing.md,
                padding: spacing.lg,
                marginTop: 10,
                backgroundColor: (colors.success || '#10B981') + '15',
                borderRadius: borderRadius.xl,
                borderWidth: 1,
                borderColor: colors.success || '#10B981',
              }}>
              <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md}}>
                <Text style={{fontSize: 18, marginRight: spacing.sm}}>✓</Text>
                <Text
                  style={{
                    fontSize: typography.fontSize.base,
                    fontWeight: '700',
                    color: colors.success || '#10B981',
                    flex: 1,
                  }}>
                  Included in Your Subscription
                </Text>
              </View>
              <Text
                style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                  lineHeight: 20,
                }}>
                This experience is included in your active subscription. No show fee may apply as per our cancellation
                policy.
              </Text>
            </Animated.View>
          ) : (
            <Animated.View
              entering={FadeInUp.delay(300).duration(600)}
              style={eventActivePlan ? {display: 'none'} : {
                marginHorizontal: spacing.md,
                padding: spacing.lg,
                backgroundColor: colors.primary + '08',
                borderRadius: borderRadius.xl,
                borderWidth: 1,
                borderColor: colors.primary + '20',
              }}>
              {!eventActivePlan && <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md}}>
                <Text style={{fontSize: 18, marginRight: spacing.sm}}>✨</Text>
                <Text
                  style={{
                    fontSize: typography.fontSize.base,
                    fontWeight: '700',
                    color: colors.text,
                    flex: 1,
                  }}>
                  {fetchedItem?.event_service_type == 'THE_REST_TABLE'
                    ? 'Get Unlimited Dinners'
                    : fetchedItem?.event_service_type == 'CULTURAL_EDIT'
                      ? 'Get Unlimited Dinners & Cultural Events'
                      : fetchedItem?.event_service_type == 'HOUSEWARMING'
                        ? 'Get All Access & Monthly Housewarming Party'
                        : 'Get Unlimited PYXI Experiences'}
                </Text>
              </View>}

              {!eventActivePlan && <TouchableOpacity
                style={{
                  backgroundColor: colors.eventInfluencer,
                  paddingVertical: spacing.md,
                  paddingHorizontal: spacing.lg,
                  borderRadius: borderRadius.lg,
                  alignItems: 'center',
                  ...shadows.sm,
                }}
                onPress={() => {
                  navigation.navigate(SCREENS.SUBSCRIPTIONS as never);
                }}>
                <Text
                  style={{
                    color: '#FFFFFF',
                    fontWeight: '700',
                    fontSize: typography.fontSize.sm,
                    letterSpacing: 0.3,
                  }}>
                  View Plans
                </Text>
              </TouchableOpacity>}
            </Animated.View>
          )}
        </ScrollView>

        {/* Footer with Total and Pay Button */}
        {ticketAvailable ? (
          <Animated.View entering={FadeInUp.delay(400).duration(600)} style={styles.footer}>
            {fetchedItem?.is_paid && (
              <View style={styles.promoSection}>
                <View style={styles.promoRow}>
                  <View style={styles.promoInputContainer}>
                    <TextInput
                      style={styles.promoInput}
                      placeholder="Enter promo code"
                      placeholderTextColor={colors.textSecondary}
                      value={promoCode}
                      onChangeText={setPromoCode}
                    />
                  </View>
                  <TouchableOpacity
                    style={[styles.applyButton, appliedPromo ? styles.removeButton : null]}
                    onPress={onApplyPromo}>
                    <Text style={styles.applyButtonText}>{appliedPromo ? 'Remove' : 'Apply'}</Text>
                  </TouchableOpacity>
                </View>
                {appliedPromo && (
                  <Text style={styles.appliedText}>
                    🎉 {appliedPromo} applied! {promoDiscountPer}% discount
                  </Text>
                )}
              </View>
            )}

            {calculateQunatity() > 1 && (
              <View style={styles.guestNote}>
                <Text style={styles.guestNoteText}>
                  📝 Note: You have to invite your guests after purchase. You will get the option via link or email and
                  they need to confirm that by clicking on the link and submitting their details.
                </Text>
              </View>
            )}

            {!isEventIncludedInSubscription && fetchedItem?.is_paid && (
              <View style={styles.paymentSummary}>
                {plusOneGuestCount > 0 && eventActivePlan?.plan?.feature_list?.bring_plus_one_fee && eventActivePlan.plan.feature_list.bring_plus_one_fee !== 'Free' && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>
                      +1 Guest ({plusOneGuestCount}x {formatPlusOneGuestFee(eventActivePlan.plan.feature_list.bring_plus_one_fee, getCurrency())})
                    </Text>
                    <Text style={styles.summaryValue}>
                      {getCurrency()}
                      {(calculatePlusOneGuestCost(plusOneGuestCount, {
                        enabled: true,
                        fee: eventActivePlan.plan.feature_list.bring_plus_one_fee,
                        description: '',
                        registrationDaysRequired: 3,
                      }) / 100).toFixed(2)}
                    </Text>
                  </View>
                )}
                {appliedPromo && (
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Discount ({promoDiscountPer}%)</Text>
                    <Text style={[styles.summaryValue, {color: colors.statusGreen}]}>
                      -{getCurrency()}
                      {((calculateTotal() * promoDiscountPer) / 100).toFixed(2)}
                    </Text>
                  </View>
                )}
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Total</Text>
                  <Text style={styles.totalAmount}>
                    {getCurrency()}
                    {appliedPromo ? calculatePercentage(promoDiscountPer).toFixed(2) : calculateTotal().toFixed(2)}
                  </Text>
                </View>
              </View>
            )}

            {isEventIncludedInSubscription && calculateTotal() <= 0 ? (
              <TouchableOpacity
                disabled={calculateQunatity() == 0 || isLoading}
                style={[styles.payButton, (calculateQunatity() == 0 || isLoading) && styles.payButtonDisabled]}
                onPress={onPayClick}>
                {isLoading ? (
                  <ActivityIndicator color={colors.white} />
                ) : (
                  <Text style={[styles.payButtonText, {fontSize: 16}]}>
                    {calculateQunatity() > 0 ? `Join Experience` : 'Select tickets'}
                  </Text>
                )}
              </TouchableOpacity>
            ) : isEventIncludedInSubscription && calculateTotal() > 0 ? (
              <TouchableOpacity
                disabled={calculateQunatity() == 0 || isLoading}
                style={[styles.payButton, (calculateQunatity() == 0 || isLoading) && styles.payButtonDisabled]}
                onPress={onPayClick}>
                {isLoading ? (
                  <ActivityIndicator color={colors.white} />
                ) : (
                  <Text style={styles.payButtonText}>
                    Pay {getCurrency()}
                    {appliedPromo ? calculatePercentage(promoDiscountPer).toFixed(2) : calculateTotal().toFixed(2)}
                  </Text>
                )}
              </TouchableOpacity>
            ) : fetchedItem?.is_paid ? (
              <TouchableOpacity
                disabled={calculateTotal() == 0 || isLoading}
                style={[styles.payButton, (calculateTotal() == 0 || isLoading) && styles.payButtonDisabled]}
                onPress={onPayClick}>
                {isLoading ? (
                  <ActivityIndicator color={colors.white} />
                ) : (
                  <Text style={styles.payButtonText}>
                    Pay {getCurrency()}
                    {appliedPromo ? calculatePercentage(promoDiscountPer).toFixed(2) : calculateTotal()}
                  </Text>
                )}
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                disabled={calculateQunatity() == 0 || isLoading}
                style={[styles.payButton, (calculateQunatity() == 0 || isLoading) && styles.payButtonDisabled]}
                onPress={onJoinClick}>
                {isLoading ? (
                  <ActivityIndicator color={colors.white} />
                ) : (
                  <Text style={styles.payButtonText}>
                    {calculateQunatity() > 0
                      ? `Book ${calculateQunatity()} ticket${calculateQunatity() > 1 ? 's' : ''}`
                      : 'Select tickets'}
                  </Text>
                )}
              </TouchableOpacity>
            )}
          </Animated.View>
        ) : (
          !isEventLoading && (
            <Animated.View entering={FadeInUp.delay(400).duration(600)} style={styles.footer}>
              <View style={[styles.paymentSummary, {backgroundColor: colors.error + '10'}]}>
                <Text style={[styles.soldoutText, {margin: 0, textAlign: 'center'}]}>🎫 This event is sold out</Text>
                <Text style={[styles.summaryLabel, {textAlign: 'center', marginTop: spacing.xs}]}>
                  Stay tuned for upcoming events!
                </Text>
              </View>
              {false && (
                <TouchableOpacity
                  style={[styles.payButton, {backgroundColor: colors.statusBlue}]}
                  onPress={onChatClick}>
                  {isLoading ? (
                    <ActivityIndicator color={colors.white} />
                  ) : (
                    <Text style={styles.payButtonText}>Chat With Organiser</Text>
                  )}
                </TouchableOpacity>
              )}
            </Animated.View>
          )
        )}
        <FailedPayment isVisible={isModalVisible} onRetry={handleRetry} onClose={handleClose} />
        
        {/* Saved Cards Modal */}
        <SavedCardsModal
          visible={showSavedCardsModal}
          onClose={() => setShowSavedCardsModal(false)}
          cards={savedCardsData?.cards || []}
          isLoading={cardsLoading}
          onSelectCard={handleSelectSavedCard}
          onAddNewCard={() => {
            setShowSavedCardsModal(false);
            setSelectedPaymentMethodId(null); // Clear selected card when adding new
            if (pendingPaymentData) {
              processTicketPayment(pendingPaymentData.body, pendingPaymentData.selectedTickets);
            }
          }}
        />
        
        {isEventLoading && (
          <Animated.View entering={FadeInUp.duration(400)} style={styles.loaderContainer}>
            <View
              style={{
                backgroundColor: colors.white,
                padding: spacing.xl,
                borderRadius: borderRadius.lg,
                shadowColor: colors.black,
                shadowOffset: {width: 0, height: 4},
                shadowOpacity: 0.1,
                shadowRadius: 12,
                elevation: 5,
                alignItems: 'center',
              }}>
              <LoadingSpinner />
              <Text
                style={{
                  marginTop: spacing.md,
                  fontSize: typography.fontSize.base,
                  color: colors.textSecondary,
                  textAlign: 'center',
                }}>
                Loading event details...
              </Text>
            </View>
          </Animated.View>
        )}
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

export default BuyTicket;
